/**
 * Wire and merge types for the terminal session-sync API
 * (`/sync/v1/*` on the user's gateway instance — see
 * infra/openclaw/SESSION-PERSISTENCE.md).
 *
 * The document payloads are structurally generic: both the Paradex web app
 * and the Paradigm desktop app supply their own `ChatMessage` /
 * `ChatSession` / `CustomFolder` shapes, which satisfy the `*Like`
 * constraints below. The sync server itself never inspects them.
 */

/** Minimal shape a chat message must have for merging. */
export interface SyncMessageLike {
  readonly id: string;
  readonly timestamp: number;
  /**
   * This copy was cut off mid-answer (the socket went away) rather than
   * finishing. `mergeMessages` prefers a whole copy over a truncated one, so
   * a half-answer saved locally does not permanently displace the complete
   * one the gateway holds.
   */
  readonly truncated?: boolean;
}

/** Minimal shape a session index entry must have for merging. */
export interface SyncSessionLike {
  readonly sessionId: string;
  readonly createdAt: number;
  readonly updatedAt: number;
  /**
   * Custom-folder assignment, resolved field-wise at merge time: folder
   * moves don't bump `updatedAt` (they aren't chat activity), so they
   * carry their own freshness stamp in `folderUpdatedAt`. Absent on
   * records written before folder sync existed (treated as 0).
   */
  readonly customFolderId?: string;
  readonly folderUpdatedAt?: number;
  /**
   * Transcript-level tombstone: messages at or before this timestamp were
   * explicitly cleared on some device. Like `folderUpdatedAt`, a clear
   * doesn't bump `updatedAt` (it isn't chat activity), so it resolves
   * field-wise at merge time — max wins. Absent ⇒ never cleared (0).
   */
  readonly clearedAt?: number;
}

/** Minimal shape a custom folder must have for merging. */
export interface SyncFolderLike {
  readonly id: string;
  readonly createdAt: number;
}

/**
 * Deletion marker. Tombstones ride inside the index document so that a
 * deletion on device A beats a stale index upload from device B — without
 * them, a merge would resurrect deleted sessions.
 */
export interface Tombstone {
  readonly kind: 'session' | 'folder';
  readonly id: string;
  readonly deletedAt: number;
}

/** The index document: sessions list + custom folders + tombstones. */
export interface SyncIndexDoc<
  S extends SyncSessionLike,
  F extends SyncFolderLike,
> {
  readonly sessions: readonly S[];
  readonly folders: readonly F[];
  readonly deleted: readonly Tombstone[];
}

/** Per-session transcript document. */
export interface SyncMessagesDoc<M extends SyncMessageLike> {
  readonly messages: readonly M[];
}

/** Server envelope: rev is the optimistic-concurrency token (ETag). */
export interface SyncEnvelope<T> {
  readonly rev: number;
  readonly updatedAt?: string;
  readonly doc: T;
}

/** GET /sync/v1/snapshot response. */
export interface SyncSnapshot<
  M extends SyncMessageLike,
  S extends SyncSessionLike,
  F extends SyncFolderLike,
> {
  readonly index: SyncEnvelope<SyncIndexDoc<S, F>> | null;
  readonly uiState: SyncEnvelope<Record<string, unknown>> | null;
  readonly sessions: Readonly<Record<string, SyncEnvelope<SyncMessagesDoc<M>>>>;
  readonly sessionList: readonly { id: string; rev: number; bytes: number }[];
}

/** Identifies one syncable document. */
export type SyncDocRef =
  | { readonly kind: 'index' }
  | { readonly kind: 'ui-state' }
  | { readonly kind: 'session'; readonly sessionId: string };

export function syncDocKey(ref: SyncDocRef): string {
  return ref.kind === 'session' ? `session:${ref.sessionId}` : ref.kind;
}
